/*
==================================================
CATEGORY CAROUSEL MOBILE

Функция:
мобильная версия карусели.
Содержит компактную кнопку Category,
overlay-карусель root categories
и отдельный блок подкатегорий.

Родитель:
category-carousel-shell.tsx

Дочерние элементы:
нет

Использует:
- getCountValue

Примечания:
desktop логика сюда не добавляется.
Панель продолжает сворачиваться как раньше.
==================================================
*/

"use client"

import { useEffect, useState } from "react"
import { clx } from "@medusajs/ui"
import { getCountValue } from "./index"

type DisplayCategoryItem = {
  id: string
  name: string
  handle?: string | null
  products?: { id: string }[] | null
  children: DisplayCategoryItem[]
}

const MOBILE_PENDING_SELECTED_RESET_DELAY_MS = 500

type CategoryCarouselMobileProps = {
  rootItems: DisplayCategoryItem[]
  selectedCategory?: string
  mobileOpen: boolean
  setMobileOpen: React.Dispatch<React.SetStateAction<boolean>>
  mobileCompact: boolean
  expandedCategoryHandle: string | null
  visibleSubcategories: DisplayCategoryItem[]
  mobileRootRef: React.RefObject<HTMLDivElement | null>
  mobileScrollRef: React.RefObject<HTMLDivElement | null>
  onApplyCategory: (handle?: string | null, options?: { closeMobile?: boolean }) => void
  onMobileSubcategorySelect: (handle?: string | null) => void
  onRootCategoryClick: (item: DisplayCategoryItem) => void
}

export default function CategoryCarouselMobile({
  rootItems,
  selectedCategory,
  mobileOpen,
  setMobileOpen,
  mobileCompact,
  expandedCategoryHandle,
  visibleSubcategories,
  mobileRootRef,
  mobileScrollRef,
  onApplyCategory,
  onMobileSubcategorySelect,
  onRootCategoryClick,
}: CategoryCarouselMobileProps) {
  const [pendingSelectedHandle, setPendingSelectedHandle] = useState<string | null>(null)

  useEffect(() => {
    if (pendingSelectedHandle === null) return

    const timer = setTimeout(() => {
      setPendingSelectedHandle(null)
    }, MOBILE_PENDING_SELECTED_RESET_DELAY_MS)

    return () => clearTimeout(timer)
  }, [pendingSelectedHandle])

  const effectiveSelectedCategory =
    pendingSelectedHandle !== null ? pendingSelectedHandle : selectedCategory

  const centerMobileCard = (button: HTMLButtonElement | null) => {
    const scroller = mobileScrollRef.current
    if (!scroller || !button) return

    const cardLeft = button.offsetLeft
    const cardWidth = button.offsetWidth
    const viewportWidth = scroller.clientWidth

    const leftOffset = Math.max(
      cardLeft - Math.max((viewportWidth - cardWidth) / 2, 16),
      0
    )

    scroller.scrollTo({
      left: leftOffset,
      behavior: "smooth",
    })
  }

  const expandedCategory =
    rootItems.find((item) => item.handle === expandedCategoryHandle) || null

  const handleSubcategorySelect = (handle?: string | null) => {
    // Выбор подкатегории / View all работает как закрытие листика:
    // состояние выбранной категории не очищаем,
    // а внешний вид после выбора решает index.tsx.
    // Локальная подсветка нужна, чтобы пункт успел визуально окраситься
    // до закрытия mobile-панели.
    setPendingSelectedHandle(handle || "")
    onMobileSubcategorySelect(handle)
  }

  return (
    <div className="relative md:hidden" ref={mobileRootRef}>
      <button
        type="button"
        data-tap-sound="category"
        onClick={() => setMobileOpen((prev) => !prev)}
        className={clx(
          `
            group relative flex items-center
            transition-all duration-300
            active:scale-[0.99]
            border border-white/45
            bg-white/60
            backdrop-blur-md
            shadow-[0_6px_16px_rgba(15,23,42,0.12)]
          `,
          mobileCompact
            ? "w-auto justify-start rounded-full px-1 py-1"
            : "w-full justify-start rounded-[18px] px-2 py-1.5"
        )}
        aria-expanded={mobileOpen}
        aria-label="Open categories"
      >
        <div
          className="
            relative z-[1]
            flex h-8 w-8 shrink-0 items-center justify-center
            rounded-full
            bg-gradient-to-br from-[#183b63] to-[#0f2742]
            text-[13px] font-black text-white
            shadow-[0_8px_16px_rgba(15,23,42,0.20)]
          "
        >
          ≡
        </div>

        <div
          className={clx(
            `
              flex items-center
              overflow-hidden
              transition-all duration-300
            `,
            mobileCompact && !mobileOpen
              ? "ml-0 max-w-0 opacity-0"
              : "ml-2 w-full min-w-0 flex-1 opacity-100"
          )}
        >
          <div className="min-w-0">
            <div className="text-[14px] font-black uppercase tracking-[0.14em] text-[#1b2b46]">
              Category
            </div>

            <div className="mt-1 h-[3px] w-16 rounded-full bg-gradient-to-r from-green-500 via-yellow-500 to-orange-500 shadow-[0_0_8px_rgba(249,115,22,0.22)]" />
          </div>

          <div
            className={clx(
              `
                relative z-[1] ml-auto
                flex h-8 w-8 items-center justify-center
                rounded-full
                border border-slate-200
                bg-white/85 text-[#1b2b46]
                shadow-[0_4px_10px_rgba(15,23,42,0.08)]
                transition-all duration-300
              `,
              mobileOpen && "rotate-180"
            )}
          >
            ▾
          </div>
        </div>
      </button>

      {mobileOpen && (
        <div
          className="
            absolute left-0 right-0 top-[calc(100%+6px)]
            z-40
          "
        >
          <div
            className="
              overflow-hidden
              rounded-[22px]
              border border-white/80
              bg-[linear-gradient(180deg,rgba(255,255,255,0.97)_0%,rgba(248,250,252,0.95)_100%)]
              backdrop-blur-xl
              shadow-[0_18px_36px_rgba(15,23,42,0.14),0_8px_16px_rgba(15,23,42,0.08)]
            "
          >
            <div className="px-4 pt-4 pb-2">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] font-black uppercase tracking-[0.14em] text-[#1b2b46]/65">
                    Choose category
                  </div>

                  <div className="mt-1 h-[3px] w-20 rounded-full bg-gradient-to-r from-green-500 via-yellow-500 to-orange-500 shadow-[0_0_8px_rgba(249,115,22,0.18)]" />
                </div>

                <button
                  type="button"
                  data-tap-sound="category"
                  onClick={() => setMobileOpen(false)}
                  className="
                    flex h-8 w-8 items-center justify-center
                    rounded-full border border-slate-200
                    bg-white text-[#1b2b46]
                    shadow-[0_4px_10px_rgba(15,23,42,0.08)]
                  "
                  aria-label="Close categories"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="relative mt-2">
              <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-6 bg-gradient-to-r from-white to-transparent" />
              <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-8 bg-gradient-to-l from-white to-transparent" />

              <div
                ref={mobileScrollRef}
                className="
                  no-scrollbar flex gap-3 overflow-x-auto
                  px-4 pb-4 pt-1
                  snap-x snap-mandatory scroll-smooth
                "
              >
                {rootItems.map((item) => {
                  const isActuallyActive =
                    item.handle === ""
                      ? !effectiveSelectedCategory
                      : item.handle === effectiveSelectedCategory ||
                        item.children.some(
                          (child) => child.handle === effectiveSelectedCategory
                        )

                  const count = item.handle === "" ? null : getCountValue(item)
                  const hasChildren = item.children.length > 0
                  const isExpanded = expandedCategoryHandle === item.handle

                  return (
                    <button
                      key={item.id}
                      type="button"
                      data-tap-sound="category"
                      onClick={(event) => {
                        centerMobileCard(event.currentTarget)

                        if (item.handle === "" || !item.children.length) {
                          setPendingSelectedHandle(item.handle || "")
                        }

                        onRootCategoryClick(item)
                      }}
                      data-active-mobile-category={isActuallyActive ? "true" : "false"}
                      className={clx(
                        `
                          relative shrink-0 snap-start overflow-hidden
                          rounded-[24px] border text-left
                          transition-all duration-300
                          active:scale-[0.985]
                          min-h-[76px] w-[82%]
                          px-4 py-3
                        `,
                        isActuallyActive
                          ? `
                            border-white/80
                            bg-gradient-to-b from-white via-white to-slate-50
                            shadow-[0_14px_24px_rgba(15,23,42,0.12),inset_0_1px_0_rgba(255,255,255,0.95)]
                            -translate-y-[1px]
                          `
                          : isExpanded
                          ? `
                            border-slate-200
                            bg-gradient-to-b from-white via-white to-slate-50
                            shadow-[0_10px_20px_rgba(15,23,42,0.10),inset_0_1px_0_rgba(255,255,255,0.95)]
                          `
                          : `
                            border-slate-200
                            bg-gradient-to-b from-slate-50 via-white to-slate-100
                            shadow-[0_8px_16px_rgba(15,23,42,0.07),inset_0_1px_0_rgba(255,255,255,0.92)]
                          `
                      )}
                      aria-pressed={isActuallyActive || isExpanded}
                    >
                      <div
                        className={clx(
                          "pointer-events-none absolute inset-0 rounded-[24px]",
                          isActuallyActive || isExpanded
                            ? "bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.95),transparent_42%)]"
                            : "bg-[radial-gradient(circle_at_top_left,rgba(255,255,255,0.75),transparent_40%)]"
                        )}
                      />

                      <div
                        className={clx(
                          `
                            pointer-events-none absolute left-4 right-4 bottom-2
                            h-[4px] rounded-full transition-all duration-300
                          `,
                          isActuallyActive
                            ? "bg-gradient-to-r from-green-500 via-yellow-500 to-orange-500 shadow-[0_0_12px_rgba(249,115,22,0.30)]"
                            : "bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200"
                        )}
                      />

                      <div className="relative z-[1] flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div
                            className={clx(
                              "text-[14px] font-extrabold uppercase tracking-[0.03em] leading-[1.15]",
                              isActuallyActive
                                ? "text-[#10233d]"
                                : "text-[#1b2b46]"
                            )}
                          >
                            {item.name}
                          </div>

                          <div
                            className={clx(
                              "mt-2 text-[11px] font-medium",
                              isActuallyActive
                                ? "text-slate-600"
                                : "text-slate-500"
                            )}
                          >
                            {item.handle === ""
                              ? "Show the full product feed"
                              : hasChildren
                              ? "Tap to open subcategories"
                              : "Tap to switch category"}
                          </div>
                        </div>

                        <div className="flex shrink-0 items-center self-center">
                          {hasChildren ? (
                            <div
                              className={clx(
                                `
                                  inline-flex min-w-[50px] items-center justify-center
                                  rounded-full border px-3 py-1.5
                                  shadow-[0_8px_16px_rgba(15,23,42,0.12)]
                                  transition-all duration-300
                                `,
                                isActuallyActive
                                  ? "border-[#1e3a5f]/15 bg-gradient-to-b from-[#183b63] to-[#0f2742]"
                                  : isExpanded
                                  ? "border-slate-200 bg-gradient-to-b from-[#24476f] to-[#12304f]"
                                  : "border-slate-200 bg-white"
                              )}
                            >
                              <span
                                className={clx(
                                  "block text-[16px] leading-none font-black transition-transform duration-300",
                                  isExpanded && "rotate-180",
                                  isActuallyActive || isExpanded
                                    ? "text-white"
                                    : "text-[#1b2b46]"
                                )}
                              >
                                ▾
                              </span>
                            </div>
                          ) : (
                            <div
                              className={clx(
                                `
                                  inline-flex shrink-0 items-center justify-center
                                  rounded-full px-3 py-1
                                  text-[10px] font-black uppercase tracking-[0.10em]
                                `,
                                isActuallyActive
                                  ? "border border-[#1e3a5f]/15 bg-gradient-to-b from-[#183b63] to-[#0f2742] text-white shadow-[0_6px_12px_rgba(15,23,42,0.18)]"
                                  : "border border-slate-200 bg-white text-[#1b2b46] shadow-[0_4px_10px_rgba(15,23,42,0.06)]"
                              )}
                            >
                              {item.handle === "" ? "All" : count}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>

            {visibleSubcategories.length > 0 && (
              <div className="border-t border-slate-200/80 px-4 pb-4 pt-3">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[13px] font-black uppercase tracking-[0.10em] text-[#1b2b46]/80">
                      {expandedCategory?.name || "Category"}
                    </div>
                    <div className="mt-1 h-[3px] w-20 rounded-full bg-gradient-to-r from-green-500 via-yellow-500 to-orange-500 shadow-[0_0_8px_rgba(249,115,22,0.18)]" />
                  </div>

                  <button
  type="button"
  data-tap-sound="category"
  onClick={() => {
    handleSubcategorySelect(expandedCategoryHandle || undefined)
  }}
  className={clx(
    `
      inline-flex items-center justify-center rounded-full
      border px-3 py-1.5
      text-[10px] font-black uppercase tracking-[0.10em]
      shadow-[0_4px_10px_rgba(15,23,42,0.06)]
      transition-all duration-300
      active:scale-[0.98]
    `,
    effectiveSelectedCategory === expandedCategoryHandle
      ? "border-white/80 bg-gradient-to-b from-[#183b63] to-[#0f2742] text-white shadow-[0_10px_18px_rgba(15,23,42,0.14)]"
      : "border-slate-200 bg-white text-[#1b2b46]"
  )}
  aria-pressed={effectiveSelectedCategory === expandedCategoryHandle}
>
  View all
</button>
                </div>

                <div className="flex flex-wrap gap-2">
                  {visibleSubcategories.map((subcat) => {
                    const isSubActive = subcat.handle === effectiveSelectedCategory
                    const subCount = getCountValue(subcat)

                    return (
                      <button
                        key={subcat.id}
                        type="button"
                        data-tap-sound="category"
                        onClick={() => {
                          handleSubcategorySelect(subcat.handle)
                        }}
                        className={clx(
                          `
                            inline-flex items-center gap-2 rounded-full border px-3 py-2
                            text-left text-[12px] font-bold transition-all duration-300
                            active:scale-[0.98]
                          `,
                          isSubActive
                            ? "border-white/80 bg-gradient-to-b from-[#183b63] to-[#0f2742] text-white shadow-[0_10px_18px_rgba(15,23,42,0.14)]"
                            : "border-slate-200 bg-white text-[#1b2b46] shadow-[0_6px_12px_rgba(15,23,42,0.06)]"
                        )}
                        aria-pressed={isSubActive}
                      >
                        <span>{subcat.name}</span>
                        <span
                          className={clx(
                            "rounded-full px-2 py-0.5 text-[10px] font-black",
                            isSubActive
                              ? "bg-white/15 text-white"
                              : "bg-slate-100 text-[#1b2b46]"
                          )}
                        >
                          {subCount}
                        </span>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
